import { useMemo, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowsLeftRight, MagnifyingGlass, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const PAGE_SIZE = 20;

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function remapSearchBlob(r) {
  return [
    r.from_brand_name,
    r.from_brand_id,
    r.from_network,
    r.to_brand_name,
    r.to_brand_id,
    r.to_network,
    r.reason,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

/**
 * Active traffic routes for this publisher — same card + scroll chrome
 * as Assigned Brands / Postback Logs on the publisher detail page.
 */
export function RoutedTrafficPanel({ publisherId, remaps = [], onChanged }) {
  const [disabling, setDisabling] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const rows = useMemo(
    () => [...(remaps || [])]
      .filter((r) => (r.status || "") === "active")
      .sort((a, b) => String(b.updated_at || b.created_at || "").localeCompare(
        String(a.updated_at || a.created_at || ""),
      )),
    [remaps],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => remapSearchBlob(r).includes(q));
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const activeCount = rows.length;

  const disableRoute = async (fromBrandId) => {
    if (!publisherId || !fromBrandId) return;
    setDisabling(fromBrandId);
    try {
      await API.delete(
        `/admin/publishers/${publisherId}/traffic-remaps/${encodeURIComponent(fromBrandId)}`,
      );
      toast.success("Routing disabled");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed to disable route");
    } finally {
      setDisabling(null);
    }
  };

  return (
    <div
      className="border border-slate-200 rounded-md overflow-hidden mb-6"
      data-testid="routed-traffic-panel"
    >
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <ArrowsLeftRight size={14} className="text-slate-500 shrink-0" />
            Routed Traffic
            {activeCount > 0 && (
              <span className="text-slate-500 font-normal">
                ({search.trim() && filtered.length !== activeCount
                  ? `${filtered.length} of ${activeCount}`
                  : activeCount})
              </span>
            )}
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Tracker URLs on the source brand_id redirect to another network offer.
          </p>
        </div>
        {activeCount > 0 && (
          <div className="relative w-full sm:w-72 shrink-0">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              type="search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search brand, network, id…"
              className="h-8 pl-7 text-xs bg-white"
              data-testid="routed-traffic-search"
            />
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-slate-400">
          No routed offers yet. Use <span className="font-medium text-slate-600">Route</span> on an assigned brand to send traffic to another network.
        </div>
      ) : filtered.length === 0 ? (
        <div
          className="px-5 py-10 text-center text-sm text-slate-400"
          data-testid="routed-traffic-empty-search"
        >
          No routes match “{search.trim()}”.
        </div>
      ) : (
        <div className="max-h-[600px] overflow-auto" data-testid="routed-traffic-scroll">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 z-10">
              <tr className="border-b border-slate-200 text-left">
                <th className="px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">From offer</th>
                <th className="px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">To offer</th>
                <th className="px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Routed since</th>
                <th className="px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Reason</th>
                <th className="px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Status</th>
                <th className="px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => {
                const fromId = r.from_brand_id || "";
                return (
                  <tr
                    key={fromId}
                    className="border-b border-slate-100 last:border-b-0"
                    data-testid={`routed-traffic-row-${fromId}`}
                  >
                    <td className="px-4 py-2.5 align-top">
                      <div className="font-medium text-slate-900 truncate max-w-[220px]" title={r.from_brand_name || fromId}>
                        {r.from_brand_name || fromId}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate max-w-[220px]" title={`${r.from_network || "?"} · ${fromId}`}>
                        {r.from_network || "?"} · <span className="font-mono">{fromId}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <div className="font-medium text-slate-900 truncate max-w-[220px]" title={r.to_brand_name || r.to_brand_id}>
                        {r.to_brand_name || r.to_brand_id}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate max-w-[220px]" title={`${r.to_network || "?"} · ${r.to_brand_id || ""}`}>
                        {r.to_network || "?"} · <span className="font-mono">{r.to_brand_id}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-600 whitespace-nowrap align-top">
                      {fmtWhen(r.created_at || r.updated_at)}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 max-w-[160px] truncate align-top" title={r.reason || ""}>
                      {r.reason || "—"}
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-sky-50 text-sky-800 border border-sky-200">
                        active
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right align-top">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        disabled={disabling === fromId}
                        onClick={() => disableRoute(fromId)}
                        data-testid={`disable-route-${fromId}`}
                      >
                        <XCircle size={14} className="mr-1" />
                        {disabling === fromId ? "…" : "Remove"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {filtered.length > PAGE_SIZE && (
        <div className="px-4 py-2.5 border-t border-slate-200 bg-slate-50/40 flex items-center justify-between text-xs text-slate-600">
          <span className="font-mono">
            {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>
              Prev
            </Button>
            <span className="font-mono mx-2">Page {currentPage} / {totalPages}</span>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
